import { useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { formatRelativeTime } from '../utils/catalog';
import { getAppMarketing } from '../utils/marketing';

const { FiArrowUpRight, FiCheck, FiTarget, FiUsers, FiX } = FiIcons;

function DetailList({ icon, title, items }) {
  return (
    <section className="detail-list">
      <h3><SafeIcon icon={icon} />{title}</h3>
      <ul>
        {items.map((item) => (
          <li key={item}>
            <SafeIcon icon={FiCheck} />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function AppDetailModal({ app, onClose }) {
  useEffect(() => {
    if (!app) return undefined;
    const closeOnEscape = (event) => event.key === 'Escape' && onClose();
    document.body.classList.add('modal-open');
    window.addEventListener('keydown', closeOnEscape);

    return () => {
      document.body.classList.remove('modal-open');
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [app, onClose]);

  if (!app) return null;
  const marketing = getAppMarketing(app);
  const isComingSoon = app.status === 'coming-soon';

  return (
    <div className="detail-layer" role="presentation">
      <button className="detail-backdrop" type="button" onClick={onClose} aria-label="Close app details" />
      <section className="detail-modal" role="dialog" aria-modal="true" aria-labelledby="detail-title">
        <button className="detail-close" type="button" onClick={onClose} aria-label="Close app details">
          <SafeIcon icon={FiX} />
        </button>

        <div className="detail-visual">
          <img src={app.image || '/demo-app-preview.svg'} alt={`${app.name} product interface`} />
          <span>{app.category}</span>
        </div>

        <div className="detail-content">
          <div className="detail-heading">
            <div>
              <p className="eyebrow">// APP SPOTLIGHT</p>
              <h2 id="detail-title">{app.name}</h2>
              <p>{app.tagline}</p>
            </div>
            <span className={`detail-status ${app.status}`}>
              <span className={`status-dot ${app.status}`} />
              {isComingSoon ? 'Coming soon' : app.status}
            </span>
          </div>

          <section className="problem-panel">
            <span className="problem-icon"><SafeIcon icon={FiTarget} /></span>
            <div>
              <h3>The problem it solves</h3>
              <p>{marketing.problem}</p>
              <strong>{marketing.outcome}</strong>
            </div>
          </section>

          <div className="detail-columns">
            <DetailList icon={FiCheck} title="Core features" items={marketing.features} />
            <DetailList icon={FiUsers} title="Popular use cases" items={marketing.useCases} />
          </div>

          <div className="audience-strip">
            <SafeIcon icon={FiUsers} />
            <span><strong>Built for:</strong> {marketing.audience}</span>
          </div>

          <div className="detail-footer">
            <div className="detail-meta">
              <span className="mono">v{app.version}</span>
              <span className="mono">updated {formatRelativeTime(app.updatedAt)}</span>
              <span className="mono">{app.stats.views.toLocaleString()} views</span>
            </div>
            {isComingSoon ? (
              <span className="coming-soon-cta">Preview in development</span>
            ) : (
              <a className="primary-button detail-cta" href={app.url}>
                Open app
                <SafeIcon icon={FiArrowUpRight} />
              </a>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

export default AppDetailModal;